import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Save } from 'lucide-react';

export default function RotaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nova';
  const [form, setForm] = useState({ nome: '', descricao: '', turnos: ['Manhã'], entregador_id: '', forma_entrega: '', observacoes: '', is_ativo: true });
  const [entregadores, setEntregadores] = useState([]);
  const [formasEntrega, setFormasEntrega] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const calls = [
      api.get('/transportadoras?is_ativo=true'),
      api.get('/configuracoes/formas-entrega'),
    ];
    if (!isNew) calls.push(api.get(`/rotas/${id}`));
    Promise.all(calls).then(([ent, formas, rota]) => {
      setEntregadores(ent.data);
      const ativas = (formas.data || []).filter(f => f.is_ativo);
      setFormasEntrega(ativas);
      if (rota) {
        setForm(f => ({ ...f, ...rota.data }));
      } else if (ativas.length > 0) {
        setForm(f => ({ ...f, forma_entrega: ativas[0].nome }));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTurno = (t) => setForm(f => ({ ...f, turnos: f.turnos.includes(t) ? f.turnos.filter(x => x !== t) : [...f.turnos, t] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post('/rotas', form);
      else await api.put(`/rotas/${id}`, form);
      navigate('/admin/rotas');
    } catch (err) { setError(err.userMessage || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;

  const inputCls = "w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid focus:border-brand-primary bg-white";

  return (
    <div data-testid="rota-form">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-brand-bg"><ArrowLeft className="w-5 h-5 text-brand-text-muted" /></button>
        <h1 className="dashboard-heading text-xl">{isNew ? 'Nova Rota' : 'Editar Rota'}</h1>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}
      <div className="card-dashboard max-w-2xl space-y-4">
        <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Nome da Rota *</label><input className={inputCls} value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Ex: Rota Centro" data-testid="field-nome-rota" /></div>
        <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Descrição</label><input className={inputCls} value={form.descricao} onChange={e => set('descricao', e.target.value)} /></div>
        <div>
          <label className="block text-xs text-brand-text-muted mb-2 font-body">Turnos *</label>
          <div className="flex gap-3">
            {['Manhã', 'Tarde'].map(t => (
              <label key={t} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.turnos.includes(t)} onChange={() => toggleTurno(t)} className="rounded" />
                <span className="text-sm font-body text-brand-text">{t}</span>
              </label>
            ))}
          </div>
        </div>
        <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Forma de Entrega</label>
          <select className={inputCls} value={form.forma_entrega} onChange={e => set('forma_entrega', e.target.value)}>
            {formasEntrega.map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
          </select>
        </div>
        <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Transportadora / Entregador</label>
          <select className={inputCls} value={form.entregador_id} onChange={e => set('entregador_id', e.target.value)}>
            <option value="">Selecione...</option>
            {entregadores.map(e => <option key={e.id} value={e.id}>{e.nome || e.nome_fantasia} ({e.tipo})</option>)}
          </select>
        </div>
        <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Observações</label><textarea className={`${inputCls} h-20 resize-none`} value={form.observacoes} onChange={e => set('observacoes', e.target.value)} /></div>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_ativo} onChange={e => set('is_ativo', e.target.checked)} /><span className="text-sm font-body text-brand-text">Rota Ativa</span></label>
        <div className="flex gap-3 pt-2">
          <button onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-lg border border-brand-border font-body text-sm">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm flex items-center gap-2" data-testid="save-rota-btn">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
